import React from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { withRouter, RouteComponentProps } from 'react-router';
import Styled from 'styled';
import { Link } from 'react-router-dom';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import LinkView from 'lib/ui/link-view';

import bullhorn from 'four-nets/icons/svg/icon_advertisement.svg';
import pencil from 'four-nets/icons/svg/icon_edit.svg';

import EnhancedText from 'four-nets/ui/enhanced-text/enhanced-text';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';
import EditMyBazaarView, { EditMyBazaarViewModel } from 'four-nets/bazaar/my-bazaar/edit-my-bazaar/edit-my-bazaar-view';
import StarsView from 'four-nets/bazaar/review/stars-view';
import { UserBazaarLink } from 'four-nets/bazaar/user-bazaar/user-bazaar-link';
import { BazaarProfile, BazaarProfileStore } from 'four-nets/bazaar/bazaar';
import { User, UserStore } from 'four-nets/user/user';
import { displayedName } from 'four-nets/user/utils';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';
import Avatar from 'four-nets/avatar/avatar';
import isPrArticleUsername from 'four-nets/common/is-pr-article-username';

import defaultStyles from './bazaar-profile-view.scss';
import actionButtonStyles from './action-button-view.scss';
import editProfileStyles from './edit-profile-button-view.scss';

export interface Props extends RouteComponentProps<any> {
    user: User;
    bazaarProfile?: BazaarProfile;
    bazaarProfileStore: BazaarProfileStore;
    hideEdit?: boolean;
    styles: typeof defaultStyles;
    shopInfoTitle: React.ReactNode;
    noShopInfo: React.ReactNode;
    userStore: UserStore;
    showReviews?: () => void;
    changeType: (fromSellers: boolean) => void;
    children: any;
    linkToProfile?: boolean;
}
interface State {
    showMore: boolean;
}

@observer
class BazaarProfilePureView extends React.Component<Props, State> {
    editMyBazaarRef?: any;
    editMyBazaarViewModel: any;
    bazaarLink: any;

    constructor(props: Props) {
        super(props);

        this.editMyBazaarViewModel = new EditMyBazaarViewModel(props.bazaarProfileStore);
        this.bazaarLink = new UserBazaarLink({ user: props.user });
        this.state = {
            showMore: false,
        };
    }

    setEditMyBazaarRef = (ref: any) => {
        this.editMyBazaarRef = ref;
    };

    edit = () => {
        if (this.editMyBazaarRef && this.editMyBazaarViewModel) {
            this.editMyBazaarRef.show({
                form: this.editMyBazaarViewModel.setupForm(this.props.bazaarProfile!.shopInfo),
            });
        }
        return Promise.resolve();
    };

    editProfile = () => {
        const { username } = this.props.userStore.me;
        this.props.history.push({ pathname: `/settings/${username}/` });
        return Promise.resolve();
    };

    navigateToProfile = () => {
        const { username } = this.props.user;
        this.props.history.push({ pathname: `/blog/${username}/` });
        return Promise.resolve();
    };

    get isAuthor() {
        return this.props.userStore.me === this.props.user;
    }

    render() {
        const { bazaarProfile, user, hideEdit, shopInfoTitle, noShopInfo, linkToProfile } = this.props;

        const styles = this.props.styles!;

        const classNames = [styles.shopInfo];
        if (!bazaarProfile || !bazaarProfile.shopInfo) {
            classNames.push(styles.noShopInfo);
        }

        return (
            <React.Fragment>
                <div className={styles.profile}>
                    <div className={styles.profileInfo}>
                        <div className={styles.mainWrapper}>
                            <Avatar user={user} />
                            <div className={styles.contentWrapper}>
                                {user && !linkToProfile ? (
                                    <LinkView link={this.bazaarLink} className={styles.title}>
                                        {displayedName(user)}
                                    </LinkView>
                                ) : user ? (
                                    <Link
                                        to={
                                            user.vendor ? `/catalog/vendor/${user.username}/` : `/blog/${user.username}`
                                        }
                                        className={styles.title}
                                    >
                                        {displayedName(user)}
                                    </Link>
                                ) : null}
                                <div className={styles.stats}>
                                    <SVGIcon style={{ image: styles.icon }} src={bullhorn} />
                                    <LinkView link={this.bazaarLink} className={styles.link}>
                                        <FormattedMessage
                                            id='%(counter)s_ENTRIES'
                                            values={{
                                                counter: bazaarProfile && bazaarProfile.productsCount,
                                            }}
                                        />
                                    </LinkView>
                                </div>
                                <button onClick={this.props.showReviews} className={styles.showReviews}>
                                    <StarsView rating={bazaarProfile && bazaarProfile.rating} />
                                    <div className={styles.count}>
                                        <FormattedMessage
                                            id='%(counter)s_RATINGS'
                                            values={{
                                                counter: (bazaarProfile && bazaarProfile.ratingsCount) || 0,
                                            }}
                                        />
                                    </div>
                                </button>
                            </div>
                        </div>
                        <EditMyBazaarView
                            innerRef={this.setEditMyBazaarRef}
                            bazaarProfileViewModel={this.editMyBazaarViewModel}
                        />
                    </div>
                    <div className={styles.shopInfoWrapper}>
                        <div className={styles.shopInfoHeader}>
                            <h2 className={styles.secondaryTitle}>{shopInfoTitle}</h2>
                            {this.isAuthor ? (
                                <div className={styles.button}>
                                    <ActionButtonView
                                        styles={actionButtonStyles}
                                        icon={<SVGIcon style={{ image: editProfileStyles.icon }} src={pencil} />}
                                        onClick={this.edit}
                                    >
                                        <FormattedMessage id='BAZAAR_EDIT_RULES' />
                                    </ActionButtonView>
                                </div>
                            ) : null}
                        </div>
                        <div className={classNames.join(' ')}>
                            {bazaarProfile && bazaarProfile.shopInfo && bazaarProfile.shopInfo.trim() ? (
                                <TextShortener
                                    text={bazaarProfile.shopInfo || ''}
                                    words={this.state.showMore ? undefined : 15}
                                >
                                    {text => (
                                        <React.Fragment>
                                            <EnhancedText
                                                followLink={
                                                    bazaarProfile?.user?.vendor?.isPresentationPaid ||
                                                    isPrArticleUsername(bazaarProfile?.user?.username)
                                                }
                                            >
                                                {text}
                                            </EnhancedText>
                                            {bazaarProfile.shopInfo !== text || this.state.showMore ? (
                                                <button
                                                    className={styles.showMore}
                                                    onClick={() => {
                                                        this.setState(prevState => ({ showMore: !prevState.showMore }));
                                                    }}
                                                >
                                                    {this.state.showMore ? (
                                                        <FormattedMessage id='BUTTON_SHOW_LESS' />
                                                    ) : null}
                                                    {!this.state.showMore && bazaarProfile.shopInfo !== text ? (
                                                        <FormattedMessage id='BUTTON_SHOW_MORE' />
                                                    ) : null}
                                                </button>
                                            ) : null}
                                        </React.Fragment>
                                    )}
                                </TextShortener>
                            ) : (
                                noShopInfo
                            )}
                        </div>
                    </div>
                </div>
                {this.props.children}
            </React.Fragment>
        );
    }
}

export default Styled(defaultStyles)(withRouter(BazaarProfilePureView));
